import type { Rect } from '../core/geometry';
import type { PublicImageNode } from './types';

export interface ImageFrameLayout {
  destination: Rect;
  clip: Rect;
}

/** Unpublished image-profile geometry. Does not admit image nodes to any profile. */
export function imageFrameLayout(width: number, height: number, frame: PublicImageNode['frame']): ImageFrameLayout {
  for (const value of [width, height, frame.width, frame.height]) {
    if (!Number.isInteger(value) || value < 1 || value > 32768) throw new Error('Image fitting dimensions must be integers from 1 through 32768.');
  }
  if (!['fill', 'contain', 'cover'].includes(frame.fit)) throw new Error('Unknown image fitting mode.');
  const clip = { x: 0, y: 0, width: frame.width, height: frame.height };
  if (frame.fit === 'fill') return { destination: { ...clip }, clip };
  // Each operation rounds to binary64 in this order. In particular, do not
  // replace scale multiplication with a different aspect-ratio expression.
  const horizontal = frame.width / width;
  const vertical = frame.height / height;
  const scale = frame.fit === 'contain' ? Math.min(horizontal, vertical) : Math.max(horizontal, vertical);
  const fittedWidth = width * scale;
  const fittedHeight = height * scale;
  const x = (frame.width - fittedWidth) / 2;
  const y = (frame.height - fittedHeight) / 2;
  return { destination: { x: x === 0 ? 0 : x, y: y === 0 ? 0 : y, width: fittedWidth, height: fittedHeight }, clip };
}

/** Apply the geometric frame clip under the caller's existing world transform.
 * Caller owns image decoding, color conversion, sampling mode and compositing.
 * A private Path2D preserves the caller's current path as well as drawing state.
 */
export function drawImageInFrame(context: CanvasRenderingContext2D, image: CanvasImageSource, intrinsicWidth: number, intrinsicHeight: number, frame: PublicImageNode['frame']): void {
  const layout = imageFrameLayout(intrinsicWidth, intrinsicHeight, frame);
  const path = new Path2D();
  path.rect(0, 0, layout.clip.width, layout.clip.height);
  context.save();
  try {
    context.clip(path);
    const rect = layout.destination;
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  } finally {
    context.restore();
  }
}
